<template>
  <div v-if="!excludedRoute && visibleAlerts.length" class="enterprise-balance-alerts">
    <div
      v-for="alert in visibleAlerts"
      :key="`${alert.pool}-${alert.cycleId}`"
      class="enterprise-balance-alert"
      :class="alert.status === 'EXHAUSTED' ? 'is-empty' : 'is-low'"
    >
      <EnterpriseIcon
        class="enterprise-balance-alert__icon"
        :src="alert.status === 'EXHAUSTED' ? octagonAlertIcon : triangleAlertIcon"
      />
      <span class="enterprise-balance-alert__text">{{ getAlertText(alert) }}</span>
      <button type="button" class="enterprise-balance-alert__settings" @click="openDialog">
        {{ $t('enterprise_ui_balance_alert_settings') }}
      </button>
      <button type="button" class="enterprise-balance-alert__buy" @click="openFuelPurchase">
        {{ alert.status === 'EXHAUSTED' ? $t('enterprise_ui_buy_now') : $t('enterprise_ui_buy_fuel_package') }}
      </button>
      <button
        v-if="alert.dismissible && alert.status !== 'EXHAUSTED'"
        type="button"
        class="enterprise-balance-alert__close"
        :aria-label="$t('enterprise_ui_balance_alert_close')"
        @click="dismiss(alert)"
      >
        <EnterpriseIcon :src="xIcon" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import EnterpriseIcon from '~/components/Index/enterprise/EnterpriseIcon.vue'
import triangleAlertIcon from '~/assets/images/enterprise/icons/triangle-alert.svg'
import octagonAlertIcon from '~/assets/images/enterprise/icons/octagon-alert.svg'
import xIcon from '~/assets/images/enterprise/icons/x.svg'
import type { EnterpriseBalanceAlertPoolSummary } from '~/services/AccountServer/types'
import type ProDialog from '~/components/Pro/ProDialog/ProDialog.vue'
import {
  getEnterpriseBalanceAlertPurchaseMode,
  isEnterpriseBalanceAlertRouteExcluded,
} from '~/utils/enterpriseBalanceAlert'

const {
  planMode,
  visibleAlerts,
  dismiss,
  openDialog,
} = useEnterpriseBalanceAlert()
const proDialogRef = inject('proDialogRef') as Ref<InstanceType<typeof ProDialog> | undefined>
const route = useRoute()
const { t } = useI18n()
const excludedRoute = computed(() => isEnterpriseBalanceAlertRouteExcluded(route.path))

function formatQuota(value: number | null | undefined) {
  const amount = Number(value || 0)
  if (!Number.isFinite(amount)) return '0'
  return amount.toLocaleString('en-US')
}

function getPoolLabel(alert: EnterpriseBalanceAlertPoolSummary) {
  if (alert.pool === 'VIP') return t('enterprise_ui_enterprise_china')
  if (alert.pool === 'AI') return t('enterprise_ui_enterprise_international')
  if (alert.label) return alert.label
  return t('enterprise_ui_characters')
}

function getAlertText(alert: EnterpriseBalanceAlertPoolSummary) {
  if (alert.status === 'EXHAUSTED') {
    if (alert.pool === 'VIP' || alert.pool === 'AI') {
      return t('enterprise_ui_balance_alert_exhausted_with_pool', { pool: getPoolLabel(alert) })
    }
    return t('enterprise_ui_balance_alert_exhausted')
  }
  if (alert.pool === 'VIP' || alert.pool === 'AI') {
    const threshold = alert.thresholdPercent
      ? `${alert.thresholdPercent}%`
      : t('enterprise_ui_chars_value', { count: formatQuota(alert.thresholdChars) })
    return t('enterprise_ui_balance_alert_low_with_pool', {
      pool: getPoolLabel(alert),
      count: formatQuota(alert.remainingWords),
      threshold,
    })
  }
  return t('enterprise_ui_balance_alert_low', { count: formatQuota(alert.remainingWords) })
}

function openFuelPurchase() {
  proDialogRef?.value?.open({ mode: 1, pricingMode: getEnterpriseBalanceAlertPurchaseMode(planMode.value) })
}
</script>

<style scoped lang="scss">
:deep(.enterprise-svg-icon) {
  display: inline-block;
  width: 1em;
  height: 1em;
  flex: 0 0 auto;
  color: inherit;
  background: currentColor;
  mask: var(--enterprise-icon-url) center / contain no-repeat;
  -webkit-mask: var(--enterprise-icon-url) center / contain no-repeat;
}

.enterprise-balance-alerts {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 20px 0;
  background: #fff;
}

.enterprise-balance-alert {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 42px;
  padding: 10px 16px;
  border: 1px solid transparent;
  border-radius: 8px;

  &__icon {
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
  }

  &__text {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.5;
  }

  button {
    flex: 0 0 auto;
    cursor: pointer;
  }

  &__settings {
    padding: 0;
    color: #2f80ed;
    font-size: 12px;
    font-weight: 600;
    background: transparent;
    border: 0;
  }

  &__buy {
    height: 30px;
    padding: 6px 14px;
    color: #fff;
    font-size: 12.5px;
    font-weight: 600;
    line-height: 18px;
    background: #2f80ed;
    border: 0;
    border-radius: 6px;
  }

  &__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    background: transparent;
    border: 0;

    :deep(.enterprise-svg-icon) {
      width: 14px;
      height: 14px;
    }
  }

  &.is-low {
    color: #92400e;
    background: #fff7e6;
    border-color: #fde68a;
  }

  &.is-empty {
    color: #b42318;
    background: #fdecec;
    border-color: #f5c6c6;

    .enterprise-balance-alert__buy {
      background: #d92d20;
    }
  }
}
</style>
